import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  MotifVerticalNavigation,
  MotifVerticalNavigationMenu,
  MotifVerticalNavigationMenuItem,
  MotifIcon,
  MotifVerticalNavigationContent,
  MotifVerticalNavigationSubmenu,
} from "@ey-xd/motif-react";
import {
  IconoirHomeSimple,
  IconoirBook,
  IconoirGroup,
  IconoirDatabase,
  IconoirCart,
  IconoirMoneySquare,
  IconoirReports,
  IconoirShop,
  IconoirOkrs,
  IconoirUser,
} from "@ey-xd/motif-icon";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import "./SideNav.scss";
import { ApiStatus } from "../../utils/apiStatus.enum";
import DiscardModal from "../DiscardModal";
import { updateShowDiscardModalState } from "../../store/slices/CheckoutSlice";

const SideNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isHomeSelected =
    location.pathname === "/" &&
    (location.state?.tab == null || location.state?.tab === 0);

  const isCtMarketPlaceSelected = location.pathname === "/marketplace";
  const isMyWorkspaceSelected =
    location.pathname.indexOf("/my-workspace") === 0;
  const isReportingSelected = location.pathname === "/report";

  const isAllOrderBundlesSelected =
    location.pathname === "/my-workspace/all-order-bundles";
  const isMyOrderesSelected = location.pathname === "/my-workspace/my-orders";
  const isMyEstimatesSelected =
    location.pathname === "/my-workspace/my-estimates";
  const isMyDraftSelected = location.pathname === "/my-workspace/my-drafts";
  const isEngagementDelegationSelected =
    location.pathname === "/my-workspace/engagement-delegation";

  const isAdminSelected = location.pathname.indexOf("/admin") === 0;
  const isAdminSupportSelected = location.pathname === "/admin/support";
  // const isEngagementOverrideSelected =
  //   location.pathname === "/admin/engagement-override";
  const isCTBMSelected = location.pathname === "/admin/ct-bm";

  /** Orders tab */
  const isOrderPipelineSelected =
    location.pathname.indexOf("/order-pipeline") === 0;
  const isEstimatesSelected = location.pathname === "/order-pipeline/estimates";
  const isOrdersSelected = location.pathname === "/order-pipeline/orders";
  const isApprovalsSelected = location.pathname === "/order-pipeline/approvals";
  const isCancelledOrdersSelected =
    location.pathname === "/order-pipeline/cancelled-orders";
  const isReturnsSelected = location.pathname === "/order-pipeline/returns";
  const isRecoveriesSelected =
    location.pathname === "/order-pipeline/recoveries";
  const isAutoTransactSelected =
    location.pathname === "/order-pipeline/auto-transact";

  const dispatch = useDispatch();

  const userPermissionsData: string[] = useSelector(
    (state: RootState) => state.UserProfileSlice.userPermissions,
  );

  const userPermissionsStatus: ApiStatus = useSelector(
    (state: RootState) => state.UserProfileSlice.userPermissionsStatus,
  );

  const isEstimateChanged =
    useSelector((state: RootState) => state.CheckoutSlice.isEstimateChanged) ||
    false;

  const showDiscardModal =
    useSelector((state: RootState) => state.CheckoutSlice.showDiscardModal) ||
    false;

  const handleDisplaySKUDB = useMemo(() => {
    if (userPermissionsStatus !== ApiStatus.SUCCESS) return false;

    if (!userPermissionsData?.length) return false;

    const skuPermissions = [
      "product_commownerproxy",
      "product_commowner",
      "system_bmo",
    ];
    return userPermissionsData.some((permission) =>
      skuPermissions.includes(permission),
    );
  }, [userPermissionsData, userPermissionsStatus]);

  const isConsumptionManager = useMemo(() => {
    if (handleDisplaySKUDB) return false;
    return userPermissionsData?.includes("sku_consumptionmgr") ?? false;
  }, [handleDisplaySKUDB, userPermissionsData]);

  const hasEngagementOwnerAccess = useMemo(() => {
    if (!userPermissionsData?.length) return false;

    const engagementPermissions = ["engagement_partner", "engagement_delegate"];

    return userPermissionsData.some((permission) =>
      engagementPermissions.includes(permission?.toLowerCase()),
    );
  }, [userPermissionsData]);

  // Memoize permission checks for consumption features
  const hasConsumptionPermissions = useMemo(() => {
    const consumptionPermissions = [
      "product_commownerproxy",
      "product_commowner",
      "sku_consumptionmgr",
    ];
    return (
      userPermissionsData?.some((permission) =>
        consumptionPermissions.includes(permission),
      ) ?? false
    );
  }, [userPermissionsData]);

  const handleClick = (path: string) => {
    if (isEstimateChanged) {
      sessionStorage.setItem("redirectTo", path);
      dispatch(updateShowDiscardModalState(true));
    }
    if (!isEstimateChanged) {
      navigate(path);
    }
  };

  const hasAdminPermission = useMemo(() => {
    if (userPermissionsStatus !== ApiStatus.SUCCESS) return false;
    if (!userPermissionsData?.length) return false;

    return userPermissionsData.some(
      (permission) => permission?.toLowerCase() === "system_admin",
    );
  }, [userPermissionsData, userPermissionsStatus]);

  const hasSupportPermission = useMemo(() => {
    if (userPermissionsStatus !== ApiStatus.SUCCESS) return false;
    if (!userPermissionsData?.length) return false;

    return userPermissionsData.some(
      (permission) => permission?.toLowerCase() === "system_support",
    );
  }, [userPermissionsData, userPermissionsStatus]);

  const hasCTBMAccess = hasAdminPermission || hasSupportPermission;

  const adminMenuItems = hasCTBMAccess
    ? [
        <MotifVerticalNavigationSubmenu
          key="admin-submenu"
          icons={{
            title: (
              <MotifIcon iconFunction={IconoirUser} size="16" title="Admin" />
            ),
            expandToggle: null,
          }}
          title="Admin"
          selected={isAdminSelected}
          className="admin-submenu-divider"
        >
          {hasAdminPermission
            ? [
                <MotifVerticalNavigationMenuItem
                  key="admin-support"
                  label="Admin & support"
                  onClick={() => handleClick("/admin/support")}
                  className="padding-left-0"
                  selected={isAdminSupportSelected}
                >
                  Admin & support
                </MotifVerticalNavigationMenuItem>,
              ]
            : []}
          {/* <MotifVerticalNavigationMenuItem
            label="Engagement Override"
            onClick={() => handleClick("/admin/engagement-override")}
            className="padding-left-0"
            selected={isEngagementOverrideSelected}
          >
            Engagement Override
          </MotifVerticalNavigationMenuItem>*/}
          {hasCTBMAccess
            ? [
                <MotifVerticalNavigationMenuItem
                  key="ct-bm"
                  label="CT BM"
                  onClick={() => handleClick("/admin/ct-bm")}
                  className="padding-left-0"
                  selected={isCTBMSelected}
                >
                  CT Business Management
                </MotifVerticalNavigationMenuItem>,
              ]
            : []}
        </MotifVerticalNavigationSubmenu>,
      ]
    : [];

  return (
    <div className="sidenav-container">
      <MotifVerticalNavigation className="sidenav-wrapper" collapse={false}>
        <MotifVerticalNavigationContent>
          {handleDisplaySKUDB || isConsumptionManager ? (
            <MotifVerticalNavigationMenu>
              <MotifVerticalNavigationMenuItem
                icon={
                  <MotifIcon
                    iconFunction={IconoirHomeSimple}
                    size="20"
                    title="Home"
                  />
                }
                label="Home"
                selected={isHomeSelected}
                onClick={() => handleClick("/")}
              >
                Home
              </MotifVerticalNavigationMenuItem>
              <MotifVerticalNavigationMenuItem
                icon={
                  <MotifIcon
                    iconFunction={IconoirShop}
                    size="20"
                    title="Marketplace"
                  />
                }
                label="Marketplace"
                selected={isCtMarketPlaceSelected}
                onClick={() => handleClick("/marketplace")}
              >
                Marketplace
              </MotifVerticalNavigationMenuItem>
              <MotifVerticalNavigationSubmenu
                icons={{
                  title: (
                    <MotifIcon
                      iconFunction={IconoirBook}
                      size="16"
                      title="My Workspace"
                    />
                  ),
                  expandToggle: null,
                }}
                title="My Workspace"
                selected={isMyWorkspaceSelected}
              >
                <MotifVerticalNavigationMenuItem
                  label="All Order Bundles"
                  // onClick={function ks() {}}
                  onClick={() => handleClick("/my-workspace/all-order-bundles")}
                  className="padding-left-0"
                  selected={isAllOrderBundlesSelected}
                >
                  All Order Bundles
                </MotifVerticalNavigationMenuItem>
                <MotifVerticalNavigationMenuItem
                  label="My Orders"
                  onClick={() => handleClick("/my-workspace/my-orders")}
                  className="padding-left-0"
                  selected={isMyOrderesSelected}
                >
                  My Orders
                </MotifVerticalNavigationMenuItem>
                <MotifVerticalNavigationMenuItem
                  label="My Estimates"
                  onClick={() => handleClick("/my-workspace/my-estimates")}
                  className="padding-left-0"
                  selected={isMyEstimatesSelected}
                >
                  My Estimates
                </MotifVerticalNavigationMenuItem>
                <MotifVerticalNavigationMenuItem
                  label="My Drafts"
                  onClick={() => handleClick("/my-workspace/my-drafts")}
                  className="padding-left-0"
                  selected={isMyDraftSelected}
                >
                  My Drafts
                </MotifVerticalNavigationMenuItem>
                {hasEngagementOwnerAccess
                  ? [
                      <MotifVerticalNavigationMenuItem
                        key="engagement-delegation"
                        label="Engagement delegation"
                        onClick={() =>
                          handleClick("/my-workspace/engagement-delegation")
                        }
                        className="padding-left-0"
                        selected={isEngagementDelegationSelected}
                      >
                        Engagement delegation
                      </MotifVerticalNavigationMenuItem>,
                    ]
                  : []}
              </MotifVerticalNavigationSubmenu>
              <MotifVerticalNavigationSubmenu
                icons={{
                  title: (
                    <MotifIcon
                      iconFunction={IconoirOkrs}
                      size="16"
                      title="Order Pipeline"
                    />
                  ),
                  expandToggle: null,
                }}
                title="Order Pipeline"
                selected={isOrderPipelineSelected}
              >
                <MotifVerticalNavigationMenuItem
                  label="Estimates"
                  onClick={() => handleClick("/order-pipeline/estimates")}
                  className="padding-left-0"
                  selected={isEstimatesSelected}
                >
                  Estimates
                </MotifVerticalNavigationMenuItem>
                <MotifVerticalNavigationMenuItem
                  label="Orders"
                  onClick={() => handleClick("/order-pipeline/orders")}
                  className="padding-left-0"
                  selected={isOrdersSelected}
                >
                  Orders
                </MotifVerticalNavigationMenuItem>
                <MotifVerticalNavigationMenuItem
                  label="Approvals"
                  onClick={() => handleClick("/order-pipeline/approvals")}
                  className="padding-left-0"
                  selected={isApprovalsSelected}
                >
                  Approvals
                </MotifVerticalNavigationMenuItem>
                <MotifVerticalNavigationMenuItem
                  label="Cancelled Orders"
                  onClick={() =>
                    handleClick("/order-pipeline/cancelled-orders")
                  }
                  className="padding-left-0"
                  selected={isCancelledOrdersSelected}
                >
                  Cancelled Orders
                </MotifVerticalNavigationMenuItem>
                <MotifVerticalNavigationMenuItem
                  label="Returns"
                  onClick={() => handleClick("/order-pipeline/returns")}
                  className="padding-left-0"
                  selected={isReturnsSelected}
                >
                  Returns
                </MotifVerticalNavigationMenuItem>
                <MotifVerticalNavigationMenuItem
                  label="Recoveries"
                  onClick={() => handleClick("/order-pipeline/recoveries")}
                  className="padding-left-0"
                  selected={isRecoveriesSelected}
                >
                  Recoveries
                </MotifVerticalNavigationMenuItem>
                <MotifVerticalNavigationMenuItem
                  label="Auto Transact"
                  onClick={() => handleClick("/order-pipeline/auto-transact")}
                  className="padding-left-0"
                  selected={isAutoTransactSelected}
                >
                  Auto Transact
                </MotifVerticalNavigationMenuItem>
              </MotifVerticalNavigationSubmenu>
              <MotifVerticalNavigationMenuItem
                icon={
                  <MotifIcon
                    iconFunction={IconoirCart}
                    size="20"
                    title="Order Bundle"
                  />
                }
                label="Order Bundle"
                selected={window.location.pathname === "/checkout"}
                onClick={() => handleClick("/checkout")}
              >
                Checkout
              </MotifVerticalNavigationMenuItem>

              <MotifVerticalNavigationMenuItem
                icon={
                  <MotifIcon
                    iconFunction={IconoirReports}
                    title="Reporting"
                    size="20"
                  />
                }
                label="Reporting"
                selected={isReportingSelected}
                onClick={() => handleClick("/report")}
              >
                Reporting
              </MotifVerticalNavigationMenuItem>
              <>
                <MotifVerticalNavigationMenuItem
                  label="SKU Database"
                  className="motif-vertical-navigation-menu-item-sku-database"
                >
                  SKU COMMERCIAL OWNER
                </MotifVerticalNavigationMenuItem>
                {hasConsumptionPermissions && (
                  <MotifVerticalNavigationMenuItem
                    icon={
                      <MotifIcon
                        iconFunction={IconoirMoneySquare}
                        size="20"
                        title="Consumption"
                      />
                    }
                    label="Consumption"
                    selected={location.pathname === "/consumption"}
                    onClick={() => handleClick("/consumption")}
                  >
                    SKU consumption
                  </MotifVerticalNavigationMenuItem>
                )}
                <MotifVerticalNavigationMenuItem
                  icon={
                    <MotifIcon
                      iconFunction={IconoirDatabase}
                      size="20"
                      title="SKU Database"
                    />
                  }
                  label="SKU Database"
                  onClick={() => handleClick("/ct/")}
                >
                  SKU Database
                </MotifVerticalNavigationMenuItem>
              </>
              {adminMenuItems}
            </MotifVerticalNavigationMenu>
          ) : (
            <MotifVerticalNavigationMenu>
              <MotifVerticalNavigationMenuItem
                icon={
                  <MotifIcon
                    iconFunction={IconoirHomeSimple}
                    size="20"
                    title="Home"
                  />
                }
                label="Home"
                selected={isHomeSelected}
                onClick={() => handleClick("/")}
              >
                Home
              </MotifVerticalNavigationMenuItem>
              <MotifVerticalNavigationMenuItem
                icon={
                  <MotifIcon
                    iconFunction={IconoirHomeSimple}
                    size="20"
                    title="Marketplace"
                  />
                }
                label="Marketplace"
                selected={isCtMarketPlaceSelected}
                onClick={() => handleClick("/marketplace")}
              >
                Marketplace
              </MotifVerticalNavigationMenuItem>
              <MotifVerticalNavigationSubmenu
                icons={{
                  title: (
                    <MotifIcon
                      iconFunction={IconoirBook}
                      size="16"
                      title="My Workspace"
                    />
                  ),
                  expandToggle: null,
                }}
                title="My Workspace"
                selected={isMyWorkspaceSelected}
              >
                <MotifVerticalNavigationMenuItem
                  label="All Order Bundles"
                  onClick={() => handleClick("/my-workspace/all-order-bundles")}
                  className="padding-left-0"
                  selected={isAllOrderBundlesSelected}
                >
                  All Order Bundles
                </MotifVerticalNavigationMenuItem>
                <MotifVerticalNavigationMenuItem
                  label="My Orders"
                  onClick={() => handleClick("/my-workspace/my-orders")}
                  className="padding-left-0"
                  selected={isMyOrderesSelected}
                >
                  My Orders
                </MotifVerticalNavigationMenuItem>
                <MotifVerticalNavigationMenuItem
                  label="My Estimates"
                  onClick={() => handleClick("/my-workspace/my-estimates")}
                  className="padding-left-0"
                  selected={isMyEstimatesSelected}
                >
                  My Estimates
                </MotifVerticalNavigationMenuItem>
                <MotifVerticalNavigationMenuItem
                  label="My Drafts"
                  onClick={() => handleClick("/my-workspace/my-drafts")}
                  className="padding-left-0"
                  selected={isMyDraftSelected}
                >
                  My Drafts
                </MotifVerticalNavigationMenuItem>
                {hasEngagementOwnerAccess
                  ? [
                      <MotifVerticalNavigationMenuItem
                        key="engagement-delegation"
                        label="Engagement delegation"
                        onClick={() =>
                          handleClick("/my-workspace/engagement-delegation")
                        }
                        className="padding-left-0"
                        selected={isEngagementDelegationSelected}
                      >
                        Engagement delegation
                      </MotifVerticalNavigationMenuItem>,
                    ]
                  : []}
              </MotifVerticalNavigationSubmenu>
              <MotifVerticalNavigationSubmenu
                icons={{
                  title: (
                    <MotifIcon
                      iconFunction={IconoirGroup}
                      size="16"
                      title="Order Pipeline"
                    />
                  ),
                  expandToggle: null,
                }}
                title="Order Pipeline"
                selected={isOrderPipelineSelected}
              >
                <MotifVerticalNavigationMenuItem
                  label="Estimates"
                  onClick={() => handleClick("/order-pipeline/estimates")}
                  className="padding-left-0"
                  selected={isEstimatesSelected}
                >
                  Estimates
                </MotifVerticalNavigationMenuItem>
                <MotifVerticalNavigationMenuItem
                  label="Orders"
                  onClick={() => handleClick("/order-pipeline/orders")}
                  className="padding-left-0"
                  selected={isOrdersSelected}
                >
                  Orders
                </MotifVerticalNavigationMenuItem>
                <MotifVerticalNavigationMenuItem
                  label="Approvals"
                  onClick={() => handleClick("/order-pipeline/approvals")}
                  className="padding-left-0"
                  selected={isApprovalsSelected}
                >
                  Approvals
                </MotifVerticalNavigationMenuItem>
                <MotifVerticalNavigationMenuItem
                  label="Cancelled Orders"
                  onClick={() =>
                    handleClick("/order-pipeline/cancelled-orders")
                  }
                  className="padding-left-0"
                  selected={isCancelledOrdersSelected}
                >
                  Cancelled Orders
                </MotifVerticalNavigationMenuItem>
                <MotifVerticalNavigationMenuItem
                  label="Returns"
                  onClick={() => handleClick("/order-pipeline/returns")}
                  className="padding-left-0"
                  selected={isReturnsSelected}
                >
                  Returns
                </MotifVerticalNavigationMenuItem>
                <MotifVerticalNavigationMenuItem
                  label="Recoveries"
                  onClick={() => handleClick("/order-pipeline/recoveries")}
                  className="padding-left-0"
                  selected={isRecoveriesSelected}
                >
                  Recoveries
                </MotifVerticalNavigationMenuItem>
                <MotifVerticalNavigationMenuItem
                  label="Auto Transact"
                  onClick={() => handleClick("/order-pipeline/auto-transact")}
                  className="padding-left-0"
                  selected={isAutoTransactSelected}
                >
                  Auto Transact
                </MotifVerticalNavigationMenuItem>
              </MotifVerticalNavigationSubmenu>
              <MotifVerticalNavigationMenuItem
                icon={
                  <MotifIcon
                    iconFunction={IconoirCart}
                    size="20"
                    title="Order Bundle"
                  />
                }
                label="Order Bundle"
                selected={window.location.pathname === "/checkout"}
                onClick={() => handleClick("/checkout")}
              >
                Checkout
              </MotifVerticalNavigationMenuItem>
              <MotifVerticalNavigationMenuItem
                icon={
                  <MotifIcon
                    iconFunction={IconoirReports}
                    size="20"
                    title="Reporting"
                  />
                }
                label="Reporting"
                selected={window.location.pathname === "/report"}
                onClick={() => handleClick("/report")}
              >
                Reporting
              </MotifVerticalNavigationMenuItem>
              {adminMenuItems}
            </MotifVerticalNavigationMenu>
          )}
        </MotifVerticalNavigationContent>
        {showDiscardModal && (
          <DiscardModal
            isVisible={showDiscardModal}
            setIsVisible={() => dispatch(updateShowDiscardModalState(false))}
            isChangedActive={isEstimateChanged}
          />
        )}
      </MotifVerticalNavigation>
    </div>
  );
};

export default SideNav;
